'use client';

import React, { useEffect, useRef, useState } from 'react';
import { TbExternalLink } from 'react-icons/tb';

const DESKTOP_VIEWPORT = { width: 1440, height: 900 };
const MOBILE_VIEWPORT = { width: 390, height: 844 };

interface PreviewImage {
    url?: string | null;
    sizes?: {
        card?: { url?: string | null };
        hero?: { url?: string | null };
    } | null;
}

interface LiveWebsitePreviewProps {
    url: string;
    title?: string;
    visitLabel?: string;
    className?: string;
    /** Portrait screenshot. On phones this replaces the embed entirely. */
    previewMobile?: PreviewImage | null;
    /** Landscape screenshot, painted instantly behind the desktop embed. */
    previewDesktop?: PreviewImage | null;
}

export default function LiveWebsitePreview({
    url,
    title,
    visitLabel = 'Visit Site',
    className = '',
    previewMobile,
    previewDesktop,
}: LiveWebsitePreviewProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    // Measured on the preview area itself (below the chrome bar), not the
    // outer card, so the scale/crop math lines up with what's visible.
    const previewRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    /**
     * Tri-state on purpose. `null` means "not measured yet" — during SSR and the
     * first client render we do not know the viewport, and defaulting to desktop
     * would mount the iframe on a phone for a frame or two. Since embedding a
     * client site costs several MB, a frame is enough to do the damage: the
     * request is already in flight before the effect corrects it.
     */
    const [isMobileDevice, setIsMobileDevice] = useState<boolean | null>(null);
    const [isVisible, setIsVisible] = useState(false);

    // Match the visitor's own device so the embedded site renders at the
    // right viewport: desktop visitors see the desktop layout, mobile
    // visitors see the mobile layout — same as opening the site directly.
    useEffect(() => {
        const mql = window.matchMedia('(max-width: 767px)');
        const update = () => setIsMobileDevice(mql.matches);
        update();
        mql.addEventListener('change', update);
        return () => mql.removeEventListener('change', update);
    }, []);

    useEffect(() => {
        const el = previewRef.current;
        if (!el) return;
        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
        });
        resizeObserver.observe(el);
        return () => resizeObserver.disconnect();
    }, []);

    // Only mount the iframe once the card is near the viewport — avoids
    // loading dozens of live sites at once on the /work page. A generous
    // rootMargin starts the fetch well before the card is scrolled into
    // view so the preview feels ready by the time it's visible.
    useEffect(() => {
        // Nothing to pre-fetch until we know this is a desktop viewport — on a
        // phone no embed will ever mount, so the observer would only burn work.
        if (isMobileDevice !== false) return;

        const el = containerRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '800px 0px' },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [isMobileDevice]);

    const viewport = isMobileDevice ? MOBILE_VIEWPORT : DESKTOP_VIEWPORT;
    const scale = containerSize.width > 0 ? containerSize.width / viewport.width : 0;

    const mobileShot = previewMobile?.sizes?.hero?.url || previewMobile?.url || null;
    const desktopShot = previewDesktop?.sizes?.hero?.url || previewDesktop?.url || null;

    /**
     * Phones never embed. The iframe was already pointer-events:none there, so
     * it only ever produced a picture — at a measured ~30 MB across these four
     * cards. A screenshot is the same picture for a fraction of a percent of the
     * bytes, and when one is missing a placeholder is still the right answer:
     * falling back to the live embed would put the cost straight back.
     */
    const embedAllowed = isMobileDevice === false;
    // Height is derived from scale so the scaled iframe always fills the
    // card exactly, cropping overflow like object-fit: cover would.
    const scaledIframeHeight = scale > 0 ? containerSize.height / scale : viewport.height;

    let hostname = url;
    try {
        hostname = new URL(url).hostname.replace(/^www\./, '');
    } catch {
        // keep raw url as fallback label
    }

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`group block ${className}`}
        >
            <div
                ref={containerRef}
                className="relative h-full w-full overflow-hidden rounded-[1.5rem] border border-brand-text/10 bg-brand-bg/10 shadow-[0_24px_70px_rgba(0,0,0,0.16)] transition-transform duration-500 group-hover:scale-[1.015]"
            >
                {/* Browser chrome bar */}
                <div className="absolute inset-x-0 top-0 z-20 flex items-center gap-2 border-b border-brand-text/10 bg-brand-bg/90 px-4 py-2.5 backdrop-blur-sm">
                    <span className="flex shrink-0 gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-red-400/60" />
                        <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/60" />
                        <span className="h-2.5 w-2.5 rounded-full bg-green-400/60" />
                    </span>
                    <span className="flex-1 truncate text-center text-xs font-body text-brand-text/60">
                        {hostname}
                    </span>
                    <TbExternalLink className="h-4 w-4 shrink-0 text-brand-text/40 transition-colors group-hover:text-brand-cta" />
                </div>

                {/* Screenshot first, live embed second (desktop only) */}
                <div ref={previewRef} className="absolute inset-x-0 bottom-0 top-9 overflow-hidden bg-white">
                    {/* Paints immediately on both breakpoints. On desktop the iframe
                        loads on top of it, so the card is never a blank white box
                        while a client site boots. */}
                    {(isMobileDevice ? mobileShot : desktopShot) && (
                        <img
                            src={(isMobileDevice ? mobileShot : desktopShot) as string}
                            alt={title || hostname}
                            loading="lazy"
                            decoding="async"
                            className="absolute inset-0 h-full w-full object-cover object-top"
                        />
                    )}

                    {/* No screenshot yet and no embed allowed — keep the card
                        deliberate rather than empty. */}
                    {!mobileShot && isMobileDevice === true && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-brand-bg px-6 text-center">
                            <span className="text-sm font-bold uppercase tracking-widest text-brand-text">
                                {title || hostname}
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-brand-cta">
                                {visitLabel}
                                <TbExternalLink className="h-3.5 w-3.5" />
                            </span>
                        </div>
                    )}

                    {embedAllowed && isVisible && scale > 0 && (
                        <iframe
                            src={url}
                            title={title || hostname}
                            loading="lazy"
                            tabIndex={-1}
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                            className="relative"
                            style={{
                                width: viewport.width,
                                height: scaledIframeHeight,
                                transform: `scale(${scale})`,
                                transformOrigin: 'top left',
                                border: 0,
                                pointerEvents: 'none',
                            }}
                        />
                    )}
                </div>

                {/* Hover reveal label */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex items-center justify-between gap-3 bg-gradient-to-t from-brand-bg/95 via-brand-bg/60 to-transparent px-5 py-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <span className="truncate text-sm font-bold uppercase tracking-widest text-brand-text">
                        {title || hostname}
                    </span>
                    <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-brand-cta">
                        {visitLabel}
                        <TbExternalLink className="h-3.5 w-3.5" />
                    </span>
                </div>
            </div>
        </a>
    );
}
